import React, { useRef } from "react"
import { Button, Dropdown, Typography } from "@mimber/ui-web"
import {
  MoreOutlined,
  TagOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@mimber/ui-web"
import { usePermissionedMenuItems } from "@/core/hooks/usePermissionedMenuItems"
import { useReactivateMemberModal } from "@/components/modals/ReactivateMemberModal"
import { useDeactivateMemberModal } from "@/components/modals/DeactivateMemberModal"
import { SuspendMemberModal } from "@/components/modals/SuspendMemberModal"
import { AssignTierToMemberModal } from "@/components/modals/AssignTierToMemberModal"
import { Resource, Action, MembershipStatus } from "@mimber/schema"
import type { PermissionedMenuItem } from "@/core/hooks/usePermissionedMenuItems"
import type { MemberActionsDropdownProps } from "./MemberActionsDropdown.types"

const { Text } = Typography

export const MemberActionsDropdown: React.FunctionComponent<
  MemberActionsDropdownProps
> = ({
  member,
  onActionComplete,
  size = "small",
  type = "text",
  iconOnly = true,
}) => {
  const assignTierOpenRef = useRef<(() => void) | null>(null)
  const suspendOpenRef = useRef<(() => void) | null>(null)

  const { contextHolder: reactivateContextHolder, showReactivateMemberModal } =
    useReactivateMemberModal(member.uuid, onActionComplete)

  const { contextHolder: deactivateContextHolder, showDeactivateMemberModal } =
    useDeactivateMemberModal(member.uuid, onActionComplete)

  const isActive = member.status === MembershipStatus.ACTIVE
  const canBeSuspended =
    member.status === MembershipStatus.ACTIVE ||
    member.status === MembershipStatus.PENDING ||
    member.status === MembershipStatus.INVITED
  const canBeReactivated =
    member.status === MembershipStatus.SUSPENDED ||
    member.status === MembershipStatus.INACTIVE ||
    member.status === MembershipStatus.LAPSED

  const menuItems: PermissionedMenuItem[] = [
    {
      key: "assign-tier",
      label: "Assign Tier",
      icon: <TagOutlined />,
      resource: Resource.MEMBERSHIP,
      action: Action.UPDATE,
      onClick: () => assignTierOpenRef.current?.(),
    },
    { type: "divider" } as PermissionedMenuItem,
    ...(canBeSuspended
      ? ([
          {
            key: "suspend",
            label: "Suspend",
            icon: <StopOutlined />,
            danger: true,
            resource: Resource.MEMBERSHIP,
            action: Action.UPDATE,
            onClick: () => suspendOpenRef.current?.(),
          },
        ] as PermissionedMenuItem[])
      : []),
    ...(canBeReactivated
      ? ([
          {
            key: "reactivate",
            label: "Reactivate",
            icon: <CheckCircleOutlined />,
            resource: Resource.MEMBERSHIP,
            action: Action.UPDATE,
            onClick: () => showReactivateMemberModal(),
          },
        ] as PermissionedMenuItem[])
      : []),
    ...(isActive
      ? ([
          {
            key: "deactivate",
            label: "Deactivate",
            icon: <ClockCircleOutlined />,
            danger: true,
            resource: Resource.MEMBERSHIP,
            action: Action.UPDATE,
            onClick: () => showDeactivateMemberModal(),
          },
        ] as PermissionedMenuItem[])
      : []),
  ]

  const { items } = usePermissionedMenuItems({ items: menuItems })

  if (items.length === 0) {
    return <Text type="secondary">&mdash;</Text>
  }

  return (
    <>
      {reactivateContextHolder}
      {deactivateContextHolder}
      <SuspendMemberModal
        memberUuid={member.uuid}
        onSuccess={onActionComplete}
        renderButton={(open) => {
          suspendOpenRef.current = open
          return <></>
        }}
      />
      <AssignTierToMemberModal
        memberUuid={member.uuid}
        currentTierUuid={member.membershipTier?.uuid ?? null}
        onSuccess={onActionComplete}
        renderButton={(open) => {
          assignTierOpenRef.current = open
          return <></>
        }}
      />
      <Dropdown menu={{ items }} trigger={["click"]}>
        <Button
          type={type}
          icon={iconOnly ? <MoreOutlined /> : undefined}
          size={size}
        >
          {!iconOnly && "Actions"}
        </Button>
      </Dropdown>
    </>
  )
}
